import { HttpErrorResponse } from '@angular/common/http';
import { JsonPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';

import { withErrorRef } from '../../core/error-ref';
import {
  AssessmentFlag,
  AssessmentProblem,
  FeedbackKind,
} from '../../core/feedback/feedback.types';
import { FeedbackService, MAX_SCREENSHOT_BYTES } from '../../core/feedback/feedback.service';
import { ScreenCaptureService } from '../../core/feedback/screen-capture.service';
import { IconComponent } from '../icon/icon';
import { ModalComponent } from '../modal/modal';

/** Which tab the dialog opens on — set by whichever menu item was chosen. */
export interface ReportDialogData {
  readonly kind: FeedbackKind;
  /**
   * FLOW-347 *Flag this*: what the form carries, filled in and not editable.
   * The dialog then asks the kind of problem (none pre-selected) and one line,
   * and sends the report tagged Assessment.
   */
  readonly flag?: AssessmentFlag;
}

/** DOC-79 §5A's choice, in order; DOC-80 §4 adds the explanation's own. */
const PROBLEMS: readonly { value: AssessmentProblem; label: string }[] = [
  { value: 'RULE_WRONG', label: 'The rule is wrong' },
  { value: 'APPLIED_WRONGLY', label: 'The assessment applied the rule wrongly' },
  { value: 'INPUT_WRONG', label: 'The input data was wrong' },
  { value: 'WORDING_WRONG', label: 'The wording on the screen is wrong' },
  { value: 'EXPLANATION_WRONG', label: 'The explanation is wrong' },
  { value: 'OTHER', label: 'Something else' },
];

const GENERIC_ERROR = 'Your report could not be sent. Please try again.';
const TOO_LARGE_ERROR =
  'Your report could not be sent because the screenshot is too large. ' +
  'Remove it, or attach a smaller one, and try again.';

/**
 * The "Report a suggestion or bug" form.
 *
 * Owns the whole act: it gathers the description and an optional screenshot,
 * submits, and closes only once the report is recorded — so a failed send
 * leaves the user's typing on screen rather than discarding it (the Tier 2 §4b
 * dialog standard the shared confirm dialog follows).
 *
 * The screenshot is optional on every device, and offered two ways because no
 * single way works everywhere: desktop browsers can photograph the screen
 * themselves, and everywhere else the user attaches one they took. See
 * {@link ScreenCaptureService}.
 */
@Component({
  selector: 'app-report-dialog',
  imports: [
    ModalComponent,
    MatButtonModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatTooltipModule,
    JsonPipe,
    ReactiveFormsModule,
    IconComponent,
  ],
  templateUrl: './report-dialog.html',
  styleUrl: './report-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<ReportDialogComponent, string | null>);
  private readonly feedback = inject(FeedbackService);
  private readonly screenCapture = inject(ScreenCaptureService);
  private readonly fb = inject(FormBuilder);

  protected readonly data = inject<ReportDialogData>(MAT_DIALOG_DATA);

  protected readonly flag = this.data.flag ?? null;

  /** The kinds of problem offered — the explanation's own only from a term. */
  protected readonly problems = PROBLEMS.filter(
    (p) => p.value !== 'EXPLANATION_WRONG' || !!this.flag?.term,
  );

  protected readonly form = this.fb.nonNullable.group({
    kind: [this.data.kind as FeedbackKind, Validators.required],
    message: ['', [Validators.required, Validators.maxLength(5000)]],
    // None pre-selected (DOC-79 §5A); required only when flagging.
    problem: ['' as AssessmentProblem | '', this.flag ? Validators.required : []],
  });

  protected readonly pending = signal(false);
  protected readonly capturing = signal(false);
  protected readonly error = signal<string | null>(null);

  /** The screenshot that will be sent, however it was obtained. */
  protected readonly screenshot = signal<Blob | null>(null);
  /** An object URL for the thumbnail, revoked whenever the screenshot changes. */
  protected readonly previewUrl = signal<string | null>(null);

  /** Whether this browser can photograph the screen itself. */
  protected readonly canCapture = this.screenCapture.isSupported();

  /** The context shown to the user, so they know exactly what they are sending. */
  protected readonly context = this.feedback.captureContext();

  protected readonly screenshotSize = computed(() => {
    const blob = this.screenshot();
    if (!blob) return '';
    return `${Math.max(1, Math.round(blob.size / 1024))} KB`;
  });

  constructor() {
    // Whatever route closes the dialog, release the preview's object URL.
    this.dialogRef.afterClosed().subscribe(() => this.revokePreview());
  }

  /** Photograph the screen. The dialog hides itself so it isn't in the shot. */
  protected async captureScreenshot(): Promise<void> {
    this.error.set(null);
    this.capturing.set(true);
    // The user is reporting the page behind this dialog, not the dialog.
    this.dialogRef.addPanelClass('report-dialog--hidden');
    try {
      const blob = await this.screenCapture.capture();
      // A null blob is a cancelled picker — an ordinary choice, no error.
      if (blob) this.acceptScreenshot(blob);
    } catch {
      this.error.set('The screenshot could not be taken. You can attach one instead.');
    } finally {
      this.dialogRef.removePanelClass('report-dialog--hidden');
      this.capturing.set(false);
    }
  }

  /** Accept a screenshot the user picked from their device. */
  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Let the same file be picked again after a removal.
    input.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.error.set('That file is not an image. Please attach a screenshot.');
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      this.error.set('That image is too large. Please attach one under 10 MB.');
      return;
    }
    this.error.set(null);
    this.acceptScreenshot(file);
  }

  protected removeScreenshot(): void {
    this.revokePreview();
    this.screenshot.set(null);
  }

  protected submit(): void {
    if (this.form.invalid || this.pending()) {
      this.form.markAllAsTouched();
      return;
    }
    this.pending.set(true);
    this.error.set(null);

    const { kind, message, problem } = this.form.getRawValue();
    const flagged = this.flag
      ? {
          tag: 'ASSESSMENT' as const,
          context: { ...this.flag, problem: problem as AssessmentProblem },
        }
      : {};
    this.feedback.submit({ kind, message, screenshot: this.screenshot(), ...flagged }).subscribe({
      next: (receipt) => this.dialogRef.close(receipt.reference),
      error: (err: HttpErrorResponse) => {
        // Stay open with their typing intact so they can retry.
        this.pending.set(false);
        this.error.set(err.status === 413 ? TOO_LARGE_ERROR : withErrorRef(GENERIC_ERROR, err));
      },
    });
  }

  protected readonly dismissReport = (): void => this.dialogRef.close(null);

  private acceptScreenshot(blob: Blob): void {
    this.revokePreview();
    this.screenshot.set(blob);
    this.previewUrl.set(URL.createObjectURL(blob));
  }

  private revokePreview(): void {
    const url = this.previewUrl();
    if (url) URL.revokeObjectURL(url);
    this.previewUrl.set(null);
  }
}
